import React, { useEffect, useState } from 'react';
import { Target } from 'lucide-react';
import { KpiCard, LoadingDots, EmptyState } from '../ui';
import { fmtCompact, fmtUsd } from '../../utils/formatNumbers';
import {
  getChannelIntelligenceOverview,
  NetworkOverview,
} from '../../services/channelIntelligenceApi';

const fmtMonthLabel = (m: string): string => {
  const x = /^(\d{4})-(\d{2})$/.exec(m);
  if (!x) return m;
  const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${names[+x[2] - 1]} ${x[1]}`;
};

const card: React.CSSProperties = {
  background: 'var(--bg-surface-1)', border: '1px solid var(--border-1)',
  borderRadius: 'var(--radius-lg)', padding: 16, marginTop: 16,
};
const h3: React.CSSProperties = { fontSize: 14, fontWeight: 700, margin: '0 0 10px', color: 'var(--fg-1)' };
const th: React.CSSProperties = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', color: 'var(--fg-4)', padding: '6px 10px', borderBottom: '1px solid var(--border-1)', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { fontSize: 13, padding: '6px 10px', borderBottom: '1px solid var(--border-2)', color: 'var(--fg-2)' };
const sectionHead: React.CSSProperties = { fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--fg-2)', margin: '22px 0 3px' };
const caption: React.CSSProperties = { fontSize: 12, color: 'var(--fg-4)', margin: '0 0 10px', lineHeight: 1.5, maxWidth: 820 };
const kpiGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 };

const OverviewTab: React.FC = () => {
  const [overview, setOverview] = useState<NetworkOverview | null>(null);
  const [month, setMonth] = useState<string>('');
  const [months, setMonths] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (m?: string) => {
    setLoading(true);
    setError(null);
    try {
      // First call (no month) — backend still needs a month; use the latest YT month if known.
      const target = m || month;
      if (!target) {
        // Bootstrap: ask for the most recent calendar month via a probe; if none, the
        // overview returns availableYoutubeMonths so we can re-fetch the latest.
        const probe = await getChannelIntelligenceOverview(currentMonthGuess());
        const list = probe.overview.availableYoutubeMonths;
        setMonths(list);
        const best = list[0] || currentMonthGuess();
        setMonth(best);
        const r = best === currentMonthGuess() ? probe : await getChannelIntelligenceOverview(best);
        setOverview(r.overview);
        return;
      }
      const r = await getChannelIntelligenceOverview(target);
      setOverview(r.overview);
      if (r.overview.availableYoutubeMonths.length) setMonths(r.overview.availableYoutubeMonths);
    } catch (e: any) {
      setError(e?.message || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const onMonth = (m: string) => { setMonth(m); load(m); };

  const ads = overview?.ads;
  const yt = overview?.youtube;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, color: 'var(--fg-1)' }}>Network Overview</h2>
        {months.length > 0 && (
          <select
            value={month}
            onChange={(e) => onMonth(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-1)', background: 'var(--bg-surface-1)', color: 'var(--fg-1)', fontSize: 13 }}
          >
            {months.map((m) => <option key={m} value={m}>{fmtMonthLabel(m)}</option>)}
          </select>
        )}
      </div>

      {loading && <div style={{ marginTop: 24 }}><LoadingDots /></div>}
      {error && <div style={{ marginTop: 16, color: 'var(--danger)' }}>{error}</div>}

      {!loading && overview && (
        <>
          {/* ── YouTube domain: whole network, all traffic ───────────────────── */}
          <div style={sectionHead}>📺 YouTube performance · entire network</div>
          <div style={caption}>
            Totals summed across {yt && yt.source !== 'none' ? <strong>all {yt.channelsWithData} channels</strong> : 'all channels'} with
            captured YouTube Analytics for {fmtMonthLabel(month)}. These are <strong>platform-wide</strong> figures — every view, watch-hour,
            and subscriber change — <strong>not</strong> limited to monetized traffic and <strong>not</strong> split by AdSense vs Direct Sold.
          </div>
          <div style={kpiGrid}>
            <KpiCard label="YouTube Views" value={yt && yt.source !== 'none' ? fmtCompact(yt.totals.views) : '—'} hint="All views this month · every captured channel" />
            <KpiCard label="Watch Hours" value={yt && yt.source !== 'none' ? fmtCompact(yt.totals.watchTimeHours) : '—'} hint="Total watch time (hours) this month" />
            <KpiCard label="Net Subscribers" value={yt && yt.source !== 'none' ? fmtCompact(yt.totals.netSubscribers) : '—'} hint="Subscribers gained − lost this month" />
            <KpiCard label="Channels w/ YT data" value={yt?.channelsWithData ?? 0} hint="Channels with a capture for this month" />
          </div>

          {yt && yt.source === 'none' && (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--fg-4)' }}>
              No captured YouTube analytics for {fmtMonthLabel(month)} yet. Run a YouTube backfill from the Ad Detail tab to populate this month.
            </div>
          )}

          {/* ── Direct Sold domain: GAM publisher-sold inventory only ─────────── */}
          <div style={sectionHead}>
            <Target size={16} strokeWidth={1.5} style={{ verticalAlign: '-3px', marginRight: 6 }} />
            Direct Sold advertising · GAM-sold inventory
          </div>
          <div style={caption}>
            Publisher-sold (<strong>Direct Sold</strong>) inventory only, from the ad warehouse — <strong>excludes AdSense</strong> and YouTube
            auction ads. So "impressions" here are Direct Sold ad impressions, not total YouTube views. AdSense earnings appear per-channel in the
            Channels tab, not in this network total.
          </div>
          {ads ? (
            <div style={kpiGrid}>
              <KpiCard label="Direct Sold Impressions" value={fmtCompact(ads.totals.impressions)} hint="Direct Sold ad impressions · excludes AdSense" />
              <KpiCard label="Direct Sold Media Sales" value={fmtUsd(ads.totals.revenueUsd)} featured hint="Media-sales revenue · excludes AdSense earnings" />
              <KpiCard label="Advertisers" value={ads.totals.advertiserCount} hint="Distinct advertisers this month" />
              <KpiCard label="Channels (ran ads)" value={ads.totals.channelCount} hint="Channels with Direct Sold delivery" />
              <KpiCard label="Campaigns" value={ads.totals.campaignCount} hint={ads.totals.campaignCount ? undefined : 'run enrichment to resolve'} />
            </div>
          ) : (
            <div style={{ marginTop: 2, fontSize: 13, color: 'var(--fg-4)' }}>
              No Direct Sold ad data for {fmtMonthLabel(month)} yet.
            </div>
          )}

          {yt && yt.top.length > 0 && (
            <div style={card}>
              <h3 style={h3}>Top channels by YouTube views — {fmtMonthLabel(month)}</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>Channel</th><th style={th}>Views</th><th style={th}>Watch hrs</th><th style={th}>Net subs</th>
                  </tr></thead>
                  <tbody>
                    {yt.top.map((c) => (
                      <tr key={c.channelId}>
                        <td style={td}>{c.channelName || c.channelId}</td>
                        <td style={td}>{fmtCompact(c.views)}</td>
                        <td style={td}>{fmtCompact(c.watchTimeHours)}</td>
                        <td style={td}>{fmtCompact(c.netSubscribers)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ads && ads.advertisers.length > 0 && (
            <div style={card}>
              <h3 style={h3}>Top advertisers (Direct Sold) — {fmtMonthLabel(month)}</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>Advertiser</th><th style={th}>Channels</th><th style={th}>Ad Impressions</th><th style={th}>Media Sales $</th>
                  </tr></thead>
                  <tbody>
                    {ads.advertisers.slice(0, 20).map((a) => (
                      <tr key={a.advertiserId || a.advertiser}>
                        <td style={td}>{a.advertiser}</td>
                        <td style={td}>{a.channelCount}</td>
                        <td style={td}>{fmtCompact(a.impressions)}</td>
                        <td style={td}>{fmtUsd(a.revenueUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!ads && (!yt || yt.top.length === 0) && (
            <div style={{ marginTop: 24 }}>
              <EmptyState title="No data for this month" subtitle="No ad rollup or YouTube capture exists for the selected month yet." />
            </div>
          )}
        </>
      )}
    </div>
  );
};

/** Best-guess most-recent completed month, 'YYYY-MM' (client-side; backend validates). */
function currentMonthGuess(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default OverviewTab;
